import { type JSX, useContext, useMemo, useState } from 'react'
import { capitalize } from 'remeda'

import { AnalyticsQueryColumn, AnalyticsQueryFilter } from '@hap/analytics'
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  Icon,
} from '@hap/components'

import { useDatastoreCollections } from '../datastore'
import { useSavedQueryParams } from '../saved-query'
import { AVAILABLE_FILTERS, STATIC_FILTER_OPTIONS } from './constants'
import { FilterContext } from './filter-context'
import { useFilterAccess } from './use-filter-access'

const availableFilterCategories = Object.entries(AVAILABLE_FILTERS)

export function FilterCategorySelector(): JSX.Element {
  const [search, setSearch] = useState('')
  const { data } = useDatastoreCollections()
  const { isFilterAllowed } = useFilterAccess()

  const { setField, onSelect, selectableFilters } = useContext(FilterContext)
  const { filters } = useSavedQueryParams()
  const existingFilters = filters.map((f) => f.field)
  const flattenedData = useMemo(() => {
    return selectableFilters
      ? []
      : Object.entries(data).reduce<
          { id: string; name: string; meta: Record<string, string> }[]
        >((acc, [key, values]) => {
          const friendlyTypeLabel = key === 'tags' ? 'Highlights' : capitalize(key)
          const field = Object.keys(AVAILABLE_FILTERS).find((field) => {
            return AVAILABLE_FILTERS[field].filterCollection === key
          })
          if (
            Array.isArray(values) &&
            key !== 'dnis' &&
            field &&
            isFilterAllowed(field)
          ) {
            return [
              ...acc,
              ...values.map((d) => ({
                ...d,
                meta: { ...d.meta, type: key, typeLabel: friendlyTypeLabel },
              })),
            ]
          }
          return acc
        }, [])
  }, [data, isFilterAllowed])

  const scopedFilterCategories = useMemo(() => {
    return availableFilterCategories.filter(([key]) => {
      return (
        isFilterAllowed(key) &&
        (!selectableFilters || selectableFilters.includes(key as AnalyticsQueryColumn))
      )
    })
  }, [isFilterAllowed, selectableFilters])

  const scopedStaticFilterCategories = useMemo(() => {
    return STATIC_FILTER_OPTIONS.map((filters) => {
      return filters.filter((filter) => {
        return (
          isFilterAllowed(filter.filter.field) &&
          (!selectableFilters ||
            selectableFilters.includes(filter.filter.field as AnalyticsQueryColumn))
        )
      })
    }).filter((f) => f.length > 0)
  }, [isFilterAllowed, selectableFilters])

  const matchesCategory = useMemo(
    () =>
      scopedFilterCategories
        .filter(([key, filter]) => {
          if (selectableFilters) {
            return selectableFilters.includes(key as AnalyticsQueryColumn)
          }

          return (
            !filter.onlyAllowOnce ||
            (filter.onlyAllowOnce &&
              !existingFilters.includes(key as AnalyticsQueryFilter['field']))
          )
        })
        .some(([_, filter]) =>
          filter.name.toLowerCase().includes(search.toLowerCase()),
        ) ||
      scopedStaticFilterCategories.some((category) =>
        category.some((filter) =>
          filter.label.toLowerCase().includes(search.toLowerCase()),
        ),
      ),
    [
      existingFilters,
      scopedFilterCategories,
      scopedStaticFilterCategories,
      search,
      selectableFilters,
    ],
  )

  return (
    <Command>
      <CommandInput
        autoFocus
        placeholder="Filter..."
        value={search}
        onValueChange={(value) => setSearch(value)}
      />
      <CommandList>
        {matchesCategory ? (
          <>
            <CommandGroup>
              {scopedFilterCategories
                .filter(([key, filter]) => {
                  return (
                    !filter.onlyAllowOnce ||
                    (filter.onlyAllowOnce &&
                      !existingFilters.includes(key as AnalyticsQueryFilter['field']))
                  )
                })
                .map(([key, filter]) => {
                  return (
                    <CommandItem
                      key={key}
                      value={key}
                      keywords={[filter.name]}
                      onSelect={() => setField(key as AnalyticsQueryFilter['field'])}
                      className="flex items-center gap-2"
                    >
                      <Icon name={filter.icon} weight={filter.iconWeight} />
                      <div>{filter.name}</div>
                    </CommandItem>
                  )
                })}
            </CommandGroup>

            {scopedStaticFilterCategories.length > 0 && <CommandSeparator />}

            {scopedStaticFilterCategories.map((category, index) => {
              return (
                <CommandGroup key={index}>
                  {category.map((filter) => {
                    return (
                      <CommandItem
                        key={filter.label}
                        value={filter.label}
                        keywords={[filter.label]}
                        onSelect={() => onSelect(filter.filter)}
                        className="flex items-center gap-2"
                      >
                        <Icon name={filter.icon} />
                        <div>{filter.label}</div>
                      </CommandItem>
                    )
                  })}
                  <CommandSeparator />
                </CommandGroup>
              )
            })}
          </>
        ) : (
          <CommandGroup>
            {flattenedData.map((data) => {
              return (
                <CommandItem
                  key={data.id}
                  value={data.id}
                  keywords={[data.name]}
                  onSelect={() => {
                    const field = Object.keys(AVAILABLE_FILTERS).find((key) => {
                      const filter = AVAILABLE_FILTERS[key]
                      return filter.filterCollection === data.meta.type
                    })
                    if (!field) throw new Error('Field not found')
                    onSelect({
                      op: 'in',
                      field: field as AnalyticsQueryColumn,
                      values: [data.id],
                    })
                  }}
                >
                  <div className="flex items-center gap-2 whitespace-nowrap">
                    <span>{data.meta.typeLabel}</span>
                    <Icon
                      name="triangle"
                      size={8}
                      weight="fill"
                      className="rotate-90 opacity-30"
                    />
                    <span className="truncate">{data.name}</span>
                  </div>
                </CommandItem>
              )
            })}
          </CommandGroup>
        )}
      </CommandList>
    </Command>
  )
}
